import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      let errorMessage = "An unexpected error occurred within the lattice.";
      let isPermissionError = false;

      try {
        const parsedError = JSON.parse(this.state.error?.message || "");
        if (parsedError.error && parsedError.error.includes("insufficient permissions")) {
          isPermissionError = true;
          errorMessage = `Security Protocol Violation: Insufficient permissions for ${parsedError.operationType} on ${parsedError.path}.`;
        } else if (parsedError.error && parsedError.error.includes("quota exceeded")) {
          errorMessage = "Lattice Quota Exceeded: The cloud infrastructure has reached its daily limit. This is exactly why we are building a sovereign network—to bypass these centralized restrictions. The quota will reset in 24 hours.";
        } else {
          errorMessage = parsedError.error || errorMessage;
        }
      } catch {
        const msg = this.state.error?.message || "";
        if (msg.toLowerCase().includes("quota exceeded")) {
          errorMessage = "Lattice Quota Exceeded: The cloud infrastructure has reached its daily limit. This is exactly why we are building a sovereign network—to bypass these centralized restrictions. The quota will reset in 24 hours.";
        } else if (msg) {
          errorMessage = `Lattice Drift Detected: ${msg}`;
        }
      }

      return (
        <div className="min-h-[400px] flex items-center justify-center p-6">
          <div className="glass-panel p-8 max-w-md w-full text-center border-red-500/20">
            <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center text-red-400 mx-auto mb-6">
              <AlertTriangle className="w-8 h-8" />
            </div>
            <h2 className="text-xl font-display font-bold mb-4">Structural Integrity Compromised</h2>
            <p className="text-sm text-white/40 mb-8 leading-relaxed">
              {errorMessage}
            </p>
            <button 
              onClick={() => window.location.reload()}
              className="w-full py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg flex items-center justify-center gap-2 transition-all"
            >
              <RefreshCw className="w-4 h-4" /> RE-SYNCHRONIZE LATTICE
            </button>
            {isPermissionError && (
              <p className="mt-4 text-[10px] font-mono text-white/20 uppercase">
                Contact the Sovereign Administrator for access derivation.
              </p>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
